import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  updateThesaurusGroupById as updateThesaurusGroupByIdAction,
  selectThesaurusGroupById as selectThesaurusGroupByIdAction,
  syncThesaurusItemsWithGrouops as syncThesaurusItemsWithGrouopsAction,
  clearAllSelectedItemsInThesaurus as clearAllSelectedItemsInThesaurusAction,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersActions';
import { filter as filterSelector, thesaurusGroupsForViewFactory } from './TreeViewDialogSelectors';
import {
  limitTo as limitToAction,
  exclude as excludeAction,
  toggleGroup as toggleGroupAction,
  fetchChildren as fetchChildrenAction,
  toggleSearchOptions as toggleSearchOptionsAction,
} from './TreeViewDialogActions';
import TreeViewDialog from './TreeViewDialog';

export function TreeViewDialogContainer(props) {
  const {
    filter,
    filterId,
    expandSearchOptions,
    toggleSearchOptions,
    keyifyParamsForFiltersRequest,
    selectThesaurusGroupById,
    toggleGroup,
    openCloseMap,
    fetchChildren,
    clearAllSelectedItemsInThesaurus,
    selfClose,
    limitTo,
    exclude,
    tags,
    syncThesaurusItemsWithGrouops,
  } = props;

  // Handlers
  const onToggleSearchOptions = useCallback(() => {
    if (!filterId || !keyifyParamsForFiltersRequest) {
      return;
    }
    toggleSearchOptions(filterId, keyifyParamsForFiltersRequest, !expandSearchOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId, keyifyParamsForFiltersRequest, expandSearchOptions]);

  const onClickThesaurusItem = useCallback(
    (item) => {
      if (!item || !keyifyParamsForFiltersRequest || !filterId) {
        return;
      }
      const groupId = item?.get('id');
      if (groupId) {
        selectThesaurusGroupById(keyifyParamsForFiltersRequest, filterId, groupId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterId, keyifyParamsForFiltersRequest]
  );

  const onToggelRootGroup = useCallback(() => {
    if (!filter || !filterId || !keyifyParamsForFiltersRequest || !openCloseMap) {
      return;
    }
    const thesaurusRootId = filter.get('thesaurusRootId');
    if (!thesaurusRootId) {
      return;
    }

    toggleGroup(filterId, keyifyParamsForFiltersRequest, thesaurusRootId, !openCloseMap[thesaurusRootId], openCloseMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, filterId, keyifyParamsForFiltersRequest, openCloseMap]);

  const onToggleGroup = useCallback(
    (item) => {
      if (!item || !filterId || !keyifyParamsForFiltersRequest || !openCloseMap || !filter) {
        return;
      }
      const childrenRetrievalStatus = item?.get('childrenRetrievalStatus');
      const id = item?.get('rootIds', List()).push(item?.get('id')).join();

      if (!id) {
        return;
      }

      toggleGroup(filterId, keyifyParamsForFiltersRequest, id, !openCloseMap[id], openCloseMap);

      if (childrenRetrievalStatus === 'fetched' || childrenRetrievalStatus === 'fetching') {
        return;
      }

      fetchChildren(filter, item);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, filterId, keyifyParamsForFiltersRequest, openCloseMap]
  );

  const onClickClearAllSelected = useCallback(() => {
    if (!filterId || !keyifyParamsForFiltersRequest) {
      return;
    }
    clearAllSelectedItemsInThesaurus(keyifyParamsForFiltersRequest, filterId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId, keyifyParamsForFiltersRequest]);

  const onClickLimitTo = useCallback(() => {
    if (!filter || !tags) {
      return;
    }
    selfClose();
    limitTo(filter, tags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, tags]);

  const onClickExclude = useCallback(() => {
    if (!filter || !tags) {
      return;
    }
    selfClose();
    exclude(filter, tags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, tags]);

  // Effects
  useEffect(() => {
    if (!keyifyParamsForFiltersRequest || !filterId) {
      return;
    }
    syncThesaurusItemsWithGrouops(keyifyParamsForFiltersRequest, filterId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDidUpdateEffect(() => {
    const tagsCount = tags?.count();

    if (!expandSearchOptions) {
      return;
    }

    if (tagsCount > 1) {
      return;
    }

    toggleSearchOptions(filterId, keyifyParamsForFiltersRequest, false);
  }, [tags, expandSearchOptions]);

  return (
    <TreeViewDialog
      {...props}
      onToggleSearchOptions={onToggleSearchOptions}
      onClickThesaurusItem={onClickThesaurusItem}
      onToggelRootGroup={onToggelRootGroup}
      onToggleGroup={onToggleGroup}
      onClickClearAllSelected={onClickClearAllSelected}
      onClickLimitTo={onClickLimitTo}
      onClickExclude={onClickExclude}
    />
  );
}

TreeViewDialogContainer.propTypes = {
  filter: PropTypes.instanceOf(Map),
  filterId: PropTypes.string,
  expandSearchOptions: PropTypes.bool,
  toggleSearchOptions: PropTypes.func,
  keyifyParamsForFiltersRequest: PropTypes.string,
  selectThesaurusGroupById: PropTypes.func,
  toggleGroup: PropTypes.func,
  openCloseMap: PropTypes.object,
  fetchChildren: PropTypes.func,
  clearAllSelectedItemsInThesaurus: PropTypes.func,
  selfClose: PropTypes.func,
  limitTo: PropTypes.func,
  exclude: PropTypes.func,
  tags: PropTypes.instanceOf(List),
  syncThesaurusItemsWithGrouops: PropTypes.func,
};

function mapStateToProps() {
  const { tags, visibleItems, selectedCount, thesaurusGroupsForView, thesaurusGroupsPartiallySelectedMap } =
    thesaurusGroupsForViewFactory();

  return createStructuredSelector({
    tags,
    filter: filterSelector,
    visibleItems,
    selectedCount,
    thesaurusGroupsForView,
    thesaurusGroupsPartiallySelectedMap,
  });
}

const mapDispatchToProps = {
  limitTo: limitToAction,
  exclude: excludeAction,
  toggleGroup: toggleGroupAction,
  fetchChildren: fetchChildrenAction,
  toggleSearchOptions: toggleSearchOptionsAction,
  updateThesaurusGroupById: updateThesaurusGroupByIdAction,
  selectThesaurusGroupById: selectThesaurusGroupByIdAction,
  syncThesaurusItemsWithGrouops: syncThesaurusItemsWithGrouopsAction,
  clearAllSelectedItemsInThesaurus: clearAllSelectedItemsInThesaurusAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(TreeViewDialogContainer);
